/**
 * Nested Tree List (piece 4): one expand/collapse tree over a self-referencing
 * node shape, for Term Container, Asset Manager and Knowledge Base categories.
 * A row collapses to itself plus a child count and expands in place; a leaf
 * expands to its own inline preview independent of its parent.
 */
export interface TreeNode {
  id: string;
  parentId?: string | null;
  label: string;
  count?: number;
  /** A colour token expression for the row's left bar, such as `var(--navy-primary)`. */
  barColor?: string;
}

export interface TreeRow<N extends TreeNode> {
  node: N;
  depth: number;
  childCount: number;
  expanded: boolean;
  isLeaf: boolean;
}

export function childrenOf<N extends TreeNode>(nodes: readonly N[], parentId: string | null): N[] {
  return nodes.filter((n) => (n.parentId ?? null) === parentId);
}

/** The rows in display order: a collapsed row hides its subtree, and depth reads independent of the bar. */
export function visibleRows<N extends TreeNode>(nodes: readonly N[], expanded: ReadonlySet<string>): TreeRow<N>[] {
  const out: TreeRow<N>[] = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const node of childrenOf(nodes, parentId)) {
      const children = childrenOf(nodes, node.id);
      const isOpen = expanded.has(node.id);
      out.push({ node, depth, childCount: node.count ?? children.length, expanded: isOpen, isLeaf: children.length === 0 });
      if (isOpen && children.length > 0) walk(node.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/** Everything beneath a node, so a drop onto a node's own descendant can be refused. */
export function descendantsOf<N extends TreeNode>(nodes: readonly N[], id: string): string[] {
  const out: string[] = [];
  const walk = (parentId: string) => {
    for (const child of childrenOf(nodes, parentId)) {
      if (out.includes(child.id)) continue;
      out.push(child.id);
      walk(child.id);
    }
  };
  walk(id);
  return out;
}

/**
 * A row dropped onto another row, for a host whose tree expresses a parent
 * rather than an order: the org chart drops a person onto their new manager.
 * 🔨 A drop onto the row itself or onto one of its own descendants is refused
 * here, before the host is told, because that is the move that loops the tree.
 */
export function dropOnNode<N extends TreeNode>(nodes: readonly N[], draggedId: string, targetId: string): { id: string; parentId: string } | null {
  if (draggedId === targetId) return null;
  if (!nodes.some((node) => node.id === draggedId) || !nodes.some((node) => node.id === targetId)) return null;
  if (descendantsOf(nodes, draggedId).includes(targetId)) return null;
  return { id: draggedId, parentId: targetId };
}

export function toggleExpanded(expanded: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(expanded);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** One row of a list whose caller nests its rows, in the order the list draws it (decision 8880). */
export interface NestedRow<R> {
  row: R;
  depth: number;
  childCount: number;
  /** The row holds children and shows them. */
  open: boolean;
  isLeaf: boolean;
}

/**
 * 🔒 A list's rows nested by the parent each names (decision 8880; piece 4): a
 * row whose parent is among the rows draws under it one step deeper, a row whose
 * parent is absent stands at the top, and the rows keep the order they were
 * handed in among their siblings, so a sort orders the top rows and each
 * parent's children and never moves a child from its parent. Every parent is
 * open unless `folded` names it. 🔨 A loop of parents is cut where it is found,
 * so no row is ever lost from the list.
 */
export function nestedRows<R>(rows: readonly R[], idOf: (row: R) => string, parentOf: (row: R) => string | null, folded: ReadonlySet<string>): NestedRow<R>[] {
  const ids = new Set(rows.map(idOf));
  const parent = new Map<string, string | null>();
  for (const row of rows) {
    const id = idOf(row);
    const named = parentOf(row);
    parent.set(id, named !== null && named !== id && ids.has(named) ? named : null);
  }
  for (const id of parent.keys()) {
    const seen = new Set([id]);
    let at = parent.get(id) ?? null;
    while (at !== null) {
      if (seen.has(at)) {
        if (at === id) parent.set(id, null);
        break;
      }
      seen.add(at);
      at = parent.get(at) ?? null;
    }
  }
  const byId = new Map(rows.map((row) => [idOf(row), row] as const));
  const nodes = rows.map((row) => ({ id: idOf(row), parentId: parent.get(idOf(row)) ?? null, label: '' }));
  const open = new Set([...ids].filter((id) => !folded.has(id)));
  return visibleRows(nodes, open).map((at) => ({ row: byId.get(at.node.id)!, depth: at.depth, childCount: at.childCount, open: at.expanded && !at.isLeaf, isLeaf: at.isLeaf }));
}
